import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";

/**
 * Obtiene el workspaceId basado en el estado de autenticación
 * - En desarrollo sin usuario → "local"
 * - Con usuario autenticado → SIEMPRE user.uid (las reglas de Firestore lo requieren)
 * - Sin usuario → null
 */
export function getWorkspaceId(auth) {
  const user = auth.currentUser;

  // En desarrollo sin usuario autenticado
  if (import.meta.env.DEV && !user) {
    console.log("[WORKSPACE] Using LOCAL workspaceId");
    return "local";
  }

  // Sin usuario autenticado
  if (!user) {
    console.warn("[WORKSPACE] No authenticated user");
    return null;
  }

  // Usuario autenticado → SIEMPRE devolver UID (coincide con reglas de Firestore)
  console.log("[WORKSPACE] Using uid as workspaceId:", user.uid);
  return user.uid;
}

/**
 * Crea el documento del workspace en Firestore si no existe.
 * Opcionalmente añade _meta.ownerEmail si se proporciona y es válido.
 *
 * CASO A: workspace NO existe
 * - Crear con estructura base + _meta.ownerEmail si ownerEmail es válido
 *
 * CASO B: workspace YA existe
 * - Si ya tiene _meta.ownerEmail: no modificar
 * - Si NO tiene _meta.ownerEmail y ownerEmail válido: sembrar únicamente _meta.ownerEmail
 */
export async function createWorkspaceIfMissing(workspaceId, db, ownerEmail) {
  if (!workspaceId) {
    console.warn("[WORKSPACE_CREATE] Invalid workspaceId, skipping creation");
    return false;
  }

  try {
    const workspaceRef = doc(db, "workspaces", workspaceId);
    const workspaceSnap = await getDoc(workspaceRef);

    // Normalizar ownerEmail: trim + toLowerCase solo si es string válido
    const normalizedEmail =
      typeof ownerEmail === "string" && ownerEmail.trim().length > 0
        ? ownerEmail.trim().toLowerCase()
        : null;

    if (workspaceSnap.exists()) {
      // CASO B: workspace ya existe
      console.log("[WORKSPACE_CREATE] Workspace already exists:", workspaceId);

      const existing = workspaceSnap.data();
      const existingEmail = existing?._meta?.ownerEmail;

      // Si ya tiene ownerEmail, no modificar
      if (existingEmail) {
        console.log("[WORKSPACE_CREATE] Workspace already has ownerEmail, skipping:", workspaceId);
        return true;
      }

      // Si recibimos ownerEmail válido y el workspace no lo tiene, sembrar únicamente _meta.ownerEmail
      if (normalizedEmail) {
        console.log("[WORKSPACE_CREATE] Seeding ownerEmail for existing workspace:", workspaceId);
        await setDoc(
          workspaceRef,
          {
            _meta: {
              ownerEmail: normalizedEmail
            }
          },
          { merge: true }
        );
        console.log("[WORKSPACE_CREATE] ✅ ownerEmail seeded successfully:", workspaceId);
      }

      return true;
    }

    // CASO A: Workspace no existe, crearlo
    console.log("[WORKSPACE_CREATE] Creating workspace:", workspaceId);

    const newWorkspaceData = {
      workspaceId,
      createdAt: serverTimestamp(),
      env: import.meta.env.DEV ? "local" : "prod",
      _initialized: true
    };

    // Añadir _meta.ownerEmail si es válido
    if (normalizedEmail) {
      newWorkspaceData._meta = {
        ownerEmail: normalizedEmail
      };
    }

    await setDoc(workspaceRef, newWorkspaceData);

    console.log("[WORKSPACE_CREATE] ✅ Workspace created successfully:", workspaceId);
    return true;
  } catch (error) {
    console.error("[WORKSPACE_CREATE] Error creating workspace:", error);
    throw error;
  }
}
